const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://srinivasafleet.com/api";

const SUPPLIER_PORTAL_URL =
  `${API_URL}/supplier-portal`;

const getToken = () =>
  localStorage.getItem("supplier_access_token") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") ||
  sessionStorage.getItem("supplier_access_token") ||
  "";

const normalizeMobile = (mobile) => {
  let value = String(mobile || "").replace(/\D/g, "");

  if (
    value.startsWith("91") &&
    value.length === 12
  ) {
    value = value.slice(2);
  }

  if (
    value.length === 10 &&
    /^[6-9]/.test(value)
  ) {
    return value;
  }

  return value;
};

const normalizeOtp = (otp) => {
  return String(otp || "")
    .replace(/\D/g, "")
    .slice(0, 6);
};

const resolveFileUrl = (value) => {
  if (!value) {
    return "";
  }

  const url = String(value).trim();

  if (!url) {
    return "";
  }

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }

  if (url.startsWith("/uploads/")) {
    return `https://srinivasafleet.com${url}`;
  }

  const apiOrigin = API_URL.replace(
    /\/api\/?$/,
    ""
  );

  if (url.startsWith("/")) {
    return `${apiOrigin}${url}`;
  }

  return `${apiOrigin}/${url}`;
};

const getSupplierPhotoValue = (supplier) => {
  if (
    !supplier ||
    typeof supplier !== "object"
  ) {
    return "";
  }

  return (
    supplier.profile_photo ||
    supplier.profile_photo_url ||
    supplier.photo_url ||
    supplier.photo ||
    supplier.image_url ||
    supplier.image ||
    ""
  );
};

const normalizeSupplier = (supplier) => {
  if (
    !supplier ||
    typeof supplier !== "object"
  ) {
    return null;
  }

  const photoUrl = resolveFileUrl(
    getSupplierPhotoValue(supplier)
  );

  return {
    ...supplier,
    profile_photo: photoUrl,
    profile_photo_url: photoUrl,
    photo_url: photoUrl,
  };
};

const saveSupplierProfile = (supplier) => {
  const normalized =
    normalizeSupplier(supplier);

  if (!normalized) {
    return null;
  }

  localStorage.setItem(
    "supplier_profile",
    JSON.stringify(normalized)
  );

  return normalized;
};

const getStoredSupplier = () => {
  try {
    const stored =
      localStorage.getItem(
        "supplier_profile"
      );

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);

    return normalizeSupplier(parsed);
  } catch (error) {
    console.error(
      "Unable to read stored supplier:",
      error
    );

    localStorage.removeItem(
      "supplier_profile"
    );

    return null;
  }
};

const parseResponse = async (response) => {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";

  let data = null;

  if (
    contentType.includes(
      "application/json"
    )
  ) {
    data = await response.json();
  } else {
    const text =
      await response.text();

    data = text || null;
  }

  if (!response.ok) {
    let message =
      `Request failed with status ${response.status}`;

    if (
      Array.isArray(data?.detail)
    ) {
      message = data.detail
        .map(
          (item) =>
            item?.msg ||
            item?.message ||
            String(item)
        )
        .join(", ");
    } else if (data?.detail) {
      message =
        typeof data.detail === "string"
          ? data.detail
          : JSON.stringify(
              data.detail
            );
    } else if (data?.message) {
      message = String(
        data.message
      );
    } else if (
      typeof data === "string" &&
      data
    ) {
      message = data;
    }

    if (response.status === 401) {
      localStorage.removeItem(
        "supplier_access_token"
      );

      localStorage.removeItem(
        "supplier_token_type"
      );

      localStorage.removeItem(
        "supplier_profile"
      );

      sessionStorage.removeItem(
        "supplier_access_token"
      );
    }

    throw new Error(message);
  }

  return data;
};

const request = async (
  endpoint,
  options = {}
) => {
  const token = getToken();

  const headers = {
    Accept: "application/json",

    ...(options.body
      ? {
          "Content-Type":
            "application/json",
        }
      : {}),

    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization =
      `Bearer ${token}`;
  }

  let response;

  try {
    response = await fetch(
      `${SUPPLIER_PORTAL_URL}${endpoint}`,
      {
        ...options,
        headers,
      }
    );
  } catch (error) {
    console.error(
      "Supplier API connection error:",
      error
    );

    throw new Error(
      "Unable to connect to the server. Please make sure the FastAPI backend is running."
    );
  }

  return parseResponse(response);
};

const supplierPortalService = {
  isLoggedIn: () =>
    Boolean(getToken()),

  getStoredSupplier: () =>
    getStoredSupplier(),

  resolveFileUrl,

  sendOtp: async (mobile) => {
    const cleanMobile =
      normalizeMobile(mobile);

    if (
      !/^[6-9]\d{9}$/.test(
        cleanMobile
      )
    ) {
      throw new Error(
        "Please enter a valid 10-digit mobile number."
      );
    }

    return request(
      "/auth/send-otp",
      {
        method: "POST",
        body: JSON.stringify({
          mobile: cleanMobile,
        }),
      }
    );
  },

  verifyOtp: async (
    mobile,
    otp
  ) => {
    const cleanMobile =
      normalizeMobile(mobile);

    const cleanOtp =
      normalizeOtp(otp);

    if (
      !/^[6-9]\d{9}$/.test(
        cleanMobile
      )
    ) {
      throw new Error(
        "Please enter a valid 10-digit mobile number."
      );
    }

    if (
      !/^\d{6}$/.test(cleanOtp)
    ) {
      throw new Error(
        "Please enter the 6-digit OTP."
      );
    }

    const data =
      await request(
        "/auth/verify-otp",
        {
          method: "POST",
          body: JSON.stringify({
            mobile: cleanMobile,
            otp: cleanOtp,
          }),
        }
      );

    if (data?.access_token) {
      localStorage.setItem(
        "supplier_access_token",
        data.access_token
      );

      localStorage.setItem(
        "supplier_token_type",
        data.token_type || "bearer"
      );
    }

    if (data?.supplier) {
      const storedSupplier =
        getStoredSupplier();

      const supplierPhoto =
        getSupplierPhotoValue(
          data.supplier
        ) ||
        getSupplierPhotoValue(
          storedSupplier
        );

      const otpSupplier =
        normalizeSupplier({
          ...(storedSupplier || {}),
          ...data.supplier,
          profile_photo:
            supplierPhoto,
        });

      saveSupplierProfile(
        otpSupplier
      );
    }

    return data;
  },

  getMe: async () => {
    const response =
      await request("/me");

    const supplier =
      response?.supplier ||
      response?.data?.supplier ||
      response?.data ||
      response;

    const storedSupplier =
      getStoredSupplier();

    const supplierPhoto =
      getSupplierPhotoValue(
        supplier
      ) ||
      getSupplierPhotoValue(
        storedSupplier
      );

    const normalized =
      normalizeSupplier({
        ...(storedSupplier || {}),
        ...supplier,
        profile_photo:
          supplierPhoto,
      });

    if (normalized) {
      saveSupplierProfile(
        normalized
      );

      return normalized;
    }

    return null;
  },

  getStatements: () => {
    return request(
      "/statements"
    );
  },

  getVehicles: () => {
    return request(
      "/vehicles"
    );
  },

  getVehicleYears: (
    vehicleId
  ) => {
    return request(
      `/vehicles/${vehicleId}/years`
    );
  },

  getVehicleMonths: (
    vehicleId,
    year
  ) => {
    return request(
      `/vehicles/${vehicleId}/months?year=${encodeURIComponent(
        year
      )}`
    );
  },

  getVehicleDocuments: (
    vehicleId,
    year,
    month
  ) => {
    return request(
      `/vehicles/${vehicleId}/documents?year=${encodeURIComponent(
        year
      )}&month=${encodeURIComponent(
        month
      )}`
    );
  },

  logout: () => {
    localStorage.removeItem(
      "supplier_access_token"
    );

    localStorage.removeItem(
      "supplier_token_type"
    );

    localStorage.removeItem(
      "supplier_profile"
    );

    localStorage.removeItem(
      "access_token"
    );

    localStorage.removeItem(
      "token"
    );

    sessionStorage.removeItem(
      "supplier_access_token"
    );
  },
};

export default supplierPortalService;